import { calculateSquadMetaRatingAndChemistry, roleWeightsByRoleId, } from "../utils/squadMetaRating";
const metaRatingIn = (metaRatings, playerRoleId, chemistry) => metaRatings?.find((m) => (playerRoleId === undefined || m.playerRoleId === playerRoleId) &&
    (chemistry === undefined || m.chemistry === chemistry))?.metaRating;
/**
 * A squad as the pitch draws it: the eleven starters with the position each
 * plays, the positions left empty, and the squad's meta rating and chemistry,
 * worked out with the squad builder's maths so they match the builder.
 */
export const buildSquadView = (source, { formations, chemistryProfiles, clubLinks, playerRoles }) => {
    const { formationId, playerRoleIds } = source;
    const formation = formations[formationId];
    const starters = source.players.slice(0, 11);
    // All eleven positions, a squad with gaps included.
    const positions = Array.from({ length: 11 }, (_, index) => ({
        player: starters[index],
        index,
        position: formation?.positions?.[index] ?? starters[index]?.preferredPosition ?? "",
        x: formation?.pitchCoordinates?.[index]?.x ?? 0.5,
        y: formation?.pitchCoordinates?.[index]?.y ?? 0.5,
    }));
    let { metaRating, chemistry } = source;
    let playersChemistry;
    if (formation && chemistryProfiles && Object.keys(chemistryProfiles).length) {
        try {
            const result = calculateSquadMetaRatingAndChemistry({
                players: starters,
                playerRoleIds,
                formationPositions: formation.positions,
                manager: source.manager,
                chemistryProfiles,
                clubLinks,
                roleWeights: roleWeightsByRoleId(playerRoles),
            });
            metaRating = result.metaRating;
            chemistry = result.chemistry;
            playersChemistry = result.playersChemistry;
        }
        catch {
            // Missing reference data: keep whatever figures came with the squad.
        }
    }
    const slots = [];
    const emptySlots = [];
    for (const { player, index, position, x, y } of positions) {
        const playerRoleId = playerRoleIds[index];
        if (!player?.resourceId) {
            // Without a formation there is nowhere to draw a gap.
            if (formation)
                emptySlots.push({ index, position, x, y, playerRoleId });
            continue;
        }
        const slotChemistry = playersChemistry?.[index];
        slots.push({
            player,
            index,
            position,
            x,
            y,
            chemistry: slotChemistry,
            playerRoleId,
            metaRating: metaRatingIn(player.metaRatings, playerRoleId, slotChemistry),
            // Tiers are always read at 0 chemistry, as in the squad builder.
            metaRatingZeroChem: metaRatingIn(player.metaRatings, playerRoleId, 0),
        });
    }
    return {
        name: source.name,
        formationId,
        formationLabel: formation?.label ?? "",
        slots,
        emptySlots,
        metaRating,
        chemistry,
    };
};
/**
 * The squad with one slot's player swapped, worked out again from scratch:
 * the swap can cost or gain chemistry for the players around it too, which
 * moves their meta ratings and the squad's. Every slot keeps its figures from
 * before, to show what changed.
 */
export const swapSquad = (source, before, { slotIndex, inPlayer, inMetaRating, inMetaRatingZeroChem }, maths) => {
    // The player going out, or the empty position being filled.
    const out = before.slots.find((slot) => slot.index === slotIndex) ??
        before.emptySlots.find((slot) => slot.index === slotIndex);
    if (!out)
        return undefined;
    const roleId = source.playerRoleIds[slotIndex];
    const metaRatings = inPlayer.metaRatings?.length
        ? inPlayer.metaRatings
        : inMetaRating != null
            ? [0, 1, 2, 3].map((chemistry) => ({
                // At 0 chemistry its own figure, which the tier is read from.
                metaRating: chemistry === 0
                    ? (inMetaRatingZeroChem ?? inMetaRating)
                    : inMetaRating,
                chemistry,
                chemstyleId: 0,
                playerRoleId: roleId ?? 0,
            }))
            : [];
    const players = [...source.players];
    players[slotIndex] = {
        ...inPlayer,
        metaRatings,
        // Recommended for this slot, so it plays in position.
        possiblePositions: inPlayer.possiblePositions?.length
            ? inPlayer.possiblePositions
            : [out.position],
    };
    const after = buildSquadView({ ...source, players }, maths);
    const previous = new Map(before.slots.map((slot) => [slot.index, slot]));
    return {
        ...after,
        name: before.name,
        previousMetaRating: before.metaRating,
        previousChemistry: before.chemistry,
        slots: after.slots.map((slot) => {
            const was = previous.get(slot.index);
            return {
                ...slot,
                // Without meta ratings to read, the incoming player's own figure.
                metaRating: slot.metaRating ??
                    (slot.index === slotIndex ? inMetaRating : undefined),
                previousChemistry: was?.chemistry,
                previousMetaRating: was?.metaRating,
                highlighted: slot.index === slotIndex,
            };
        }),
    };
};
/**
 * Whether recommendations were made for an older version of the squad: one
 * of them swaps out a player who no longer starts. A player moved to the
 * bench counts: the swap no longer changes the eleven on the pitch.
 */
export const recsAreStale = (recommendations, source) => {
    if (!source?.players.length)
        return false;
    const starters = new Set(source.players.slice(0, 11).map((p) => p?.resourceId));
    // A recommendation for an empty position has no player to go missing.
    return recommendations.some((rec) => !!rec.oldPlayer?.resourceId && !starters.has(rec.oldPlayer.resourceId));
};
/**
 * The slot a recommendation swaps into: the player it replaces, or, for an
 * empty position, the first empty slot of that position (and role, when
 * several share it). The backend names the position, not the slot.
 */
export const slotForRecommendation = (squad, oldPlayer) => {
    if (!squad || !oldPlayer)
        return undefined;
    if (oldPlayer.resourceId)
        return squad.slots.find((s) => s.player.resourceId === oldPlayer.resourceId);
    const samePosition = squad.emptySlots.filter((s) => s.position === oldPlayer.position);
    return (samePosition.find((s) => s.playerRoleId === oldPlayer.playerRoleId) ??
        samePosition[0]);
};
/** The active squad as `/user-squads/active` returns it, as a source. */
export const activeSquadSource = (squad) => ({
    name: squad.squadName,
    formationId: squad.tactic?.teamTactics?.formationId ?? squad.currentFormation,
    players: squad.players,
    playerRoleIds: squad.players
        .slice(0, 11)
        .map((p, i) => squad.tactic?.playerRoles?.[i]?.playerRoleId ??
        p?.metaRatings?.[0]?.playerRoleId),
    manager: squad.manager,
});
/** The best possible squad My Club builds, as a source. */
export const bestSquadSource = (squad, name) => ({
    name,
    formationId: squad.tactic.teamTactics.formationId,
    players: squad.players,
    playerRoleIds: squad.players
        .slice(0, 11)
        .map((p, i) => squad.tactic.playerRoles?.[i]?.playerRoleId ?? p?.playerRoleId),
    manager: squad.manager,
    metaRating: squad.metaRating,
    chemistry: squad.chemistry,
});
//# sourceMappingURL=squad.js.map